import { makeStyles } from '@material-ui/core/styles'
import { CALLOUT_BACKGROUND } from 'lib/form-field-styles'

export const useStyles = makeStyles(theme => ({
  dialogTitle: {
    padding: 0,
  },
  hero: {
    position: 'relative',
    minHeight: 220,
    color: theme.palette.common.white,
  },
  headerImageWrapper: {
    position: 'relative',
    height: 220,
    overflow: 'hidden',
  },
  headerImage: {
    width: '100%',
    height: 220,
    objectFit: 'cover',
    objectPosition: 'center',
    display: 'block',
  },
  headerImageOverlay: {
    position: 'absolute',
    inset: 0,
    background: `linear-gradient(
      180deg,
      rgba(0, 35, 75, 0.2) 0%,
      rgba(0, 35, 75, 0.55) 100%
    )`,
    pointerEvents: 'none',
  },
  heroContent: {
    position: 'absolute',
    left: theme.spacing(3),
    bottom: theme.spacing(3),
    right: 120,
    zIndex: 1,
  },
  heroPrice: {
    position: 'absolute',
    right: theme.spacing(3),
    bottom: theme.spacing(3),
    textAlign: 'right',
    zIndex: 1,
  },
  statusChip: {
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    color: theme.palette.common.white,
    fontSize: '0.6875rem',
    fontWeight: 700,
    letterSpacing: '0.06em',
    height: 22,
    marginBottom: theme.spacing(1),
  },
  heroTitle: {
    fontSize: '1.75rem',
    fontWeight: 700,
    lineHeight: 1.2,
    marginBottom: theme.spacing(0.5),
  },
  heroSubtitle: {
    fontSize: '0.9375rem',
    fontWeight: 500,
    opacity: 0.9,
  },
  heroPriceLabel: {
    fontSize: '0.6875rem',
    fontWeight: 700,
    letterSpacing: '0.08em',
    opacity: 0.85,
    marginBottom: theme.spacing(0.5),
  },
  heroPriceValue: {
    fontSize: '1.75rem',
    fontWeight: 700,
    lineHeight: 1,
  },
  contentRoot: {
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
    paddingLeft: theme.spacing(4),
    paddingRight: theme.spacing(4),
    backgroundColor: theme.palette.common.white,
    // Must not be 'visible' — that overrides DialogContent's scroll container.
    overflow: 'auto',
  },
  contentGrid: {
    width: '100%',
  },
  aboutColumn: {
    paddingRight: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      paddingRight: 0,
      paddingBottom: theme.spacing(3),
    },
  },
  detailsColumn: {
    paddingLeft: theme.spacing(2),
    minWidth: 0,
    [theme.breakpoints.down('sm')]: {
      paddingLeft: 0,
    },
  },
  mainColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3),
    width: '100%',
  },
  sectionLabel: {
    fontSize: '0.6875rem',
    fontWeight: 700,
    letterSpacing: '0.08em',
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1.5),
    textTransform: 'uppercase',
  },
  bodyText: {
    fontSize: '0.9375rem',
    lineHeight: 1.6,
    color: theme.palette.text.primary,
    '& p': {
      margin: 0,
      marginBottom: theme.spacing(1.5),
    },
    '& p:last-child': {
      marginBottom: 0,
    },
  },
  readMoreButton: {
    padding: 0,
    marginTop: theme.spacing(1),
    minWidth: 0,
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '0.875rem',
  },
  tagRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
  },
  tagChip: {
    backgroundColor: '#EBF0F2',
    color: '#172024',
    border: 'none',
    fontWeight: 500,
  },
  qrCodeBlock: {
    paddingTop: theme.spacing(1),
  },
  qrCodeSection: {
    display: 'inline-flex',
  },
  qrCode: {
    display: 'block',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    padding: theme.spacing(1),
    backgroundColor: theme.palette.common.white,
  },
  qrCodeCaption: {
    fontSize: '0.8125rem',
    color: theme.palette.text.secondary,
    lineHeight: 1.5,
    marginTop: theme.spacing(1),
  },
  sidebar: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    width: '100%',
    minWidth: 0,
  },
  detailsCard: {
    backgroundColor: theme.palette.common.white,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 12,
    padding: theme.spacing(3),
    minWidth: 0,
    overflow: 'hidden',
  },
  infoCard: {
    backgroundColor: theme.palette.common.white,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 12,
    padding: theme.spacing(2),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    cursor: 'pointer',
    transition: 'background-color 0.2s ease',
    '&:hover': {
      backgroundColor: 'rgba(0, 35, 75, 0.03)',
    },
  },
  infoCardContent: {
    flex: 1,
    minWidth: 0,
  },
  infoCardTitle: {
    fontSize: '0.9375rem',
    fontWeight: 600,
    lineHeight: 1.3,
  },
  infoCardSubtitle: {
    fontSize: '0.8125rem',
    color: theme.palette.text.secondary,
    lineHeight: 1.3,
  },
  detailRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    paddingTop: theme.spacing(0.75),
    paddingBottom: theme.spacing(0.75),
    marginBottom: theme.spacing(1.5),
    minWidth: 0,
    maxWidth: '100%',
    width: '100%',
    overflow: 'hidden',
    '&:last-child': {
      marginBottom: 0,
    },
  },
  iconBadge: {
    width: 36,
    height: 36,
    borderRadius: '50%',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    '& svg, & .MuiSvgIcon-root': {
      width: 18,
      height: 18,
      fontSize: 18,
    },
  },
  detailIconBadge: {
    backgroundColor: '#EBF0F2',
    color: '#172024',
    '& svg, & .MuiSvgIcon-root': {
      color: '#172024',
    },
  },
  actionIconBadge: {
    backgroundColor: '#CCDDFF',
    color: '#001C57',
    '& svg, & .MuiSvgIcon-root': {
      color: '#001C57',
    },
  },
  detailPrimary: {
    fontSize: '0.9375rem',
    fontWeight: 600,
    lineHeight: 1.4,
  },
  detailSecondary: {
    fontSize: '0.8125rem',
    color: theme.palette.text.secondary,
    lineHeight: 1.4,
  },
  locationLink: {
    fontSize: '0.9375rem',
    fontWeight: 600,
    color: theme.palette.primary.main,
    lineHeight: 1.4,
  },
  sessionsAccordion: {
    marginLeft: 48,
    marginTop: -8,
    marginBottom: theme.spacing(1),
  },
  copyTextButtonStyled: {
    textTransform: 'none',
    fontSize: 'unset',
    padding: 0,
    width: '100%',
    maxWidth: '100%',
    display: 'block',
    justifyContent: 'flex-start',
    margin: 0,
    minWidth: 0,
    overflow: 'hidden',
    '& .MuiButton-label': {
      width: '100%',
      maxWidth: '100%',
      minWidth: 0,
      display: 'block',
      overflow: 'hidden',
    },
  },
  linkText: {
    fontSize: '0.8125rem',
    color: theme.palette.primary.main,
    textAlign: 'left',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    minWidth: 0,
    maxWidth: '100%',
    flex: '1 1 0%',
    display: 'block',
  },
  actionButtonContainer: {
    position: 'absolute',
    top: theme.spacing(1.5),
    right: theme.spacing(1.5),
    display: 'flex',
    gap: theme.spacing(1),
    zIndex: 1,
  },
  actionAvatar: {
    backgroundColor: 'rgba(255, 255, 255, 0.92)',
    cursor: 'pointer',
    width: 36,
    height: 36,
  },
  hopeBanner: {
    marginBottom: theme.spacing(2),
    '& > div': {
      backgroundColor: CALLOUT_BACKGROUND,
      borderRadius: 12,
    },
  },
  paymentWarningRow: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
  errorIcon: {
    color: '#000000DE',
  },
  unableToAcceptPaymentsChip: {
    background: '#F88078',
  },
  payoutsOnHoldChip: {
    background: '#FFB547',
  },
}))
